// Read the current working project's referenced assets only. Never parse project
// markup in the parent, fetch arbitrary URLs, or enumerate browser/account storage.
const mimeTypes = {png:'image/png',jpg:'image/jpeg',jpeg:'image/jpeg',gif:'image/gif',svg:'image/svg+xml',
  webp:'image/webp',avif:'image/avif',bmp:'image/bmp',ico:'image/x-icon',woff:'font/woff',woff2:'font/woff2',ttf:'font/ttf',otf:'font/otf'};
const allowedType = /^(?:image\/(?:png|jpeg|gif|svg\+xml|webp|avif|bmp|x-icon)|font\/(?:woff2?|ttf|otf)|application\/(?:font-woff|vnd.ms-fontobject))$/;

export async function createPreviewSnapshot(root, css, imageStorage, blobURLs = {}) {
  const html = root.innerHTML;
  css += '\n' + Array.from(root.querySelectorAll('style'), node => node.textContent).join('\n');
  if (html.length > 1024 * 1024 || css.length > 256 * 1024) throw Error('Preview source exceeds its limits.');
  const references = new Map(); let referenceCharacters = 0;
  const byURL = new Map(Object.keys(blobURLs).map(key => [blobURLs[key], key]));
  function keyFor(reference) {
    if (typeof reference !== 'string' || reference.length > 2048 || /[\u0000-\u001f\u007f\\]/.test(reference)) return null;
    if (byURL.has(reference)) return byURL.get(reference);
    let value = reference.replace(/^(?:asset:|data-image:)/, '');
    if (/^[a-z][a-z0-9+.-]*:|^\/\//i.test(value)) return null;
    value = value.split(/[?#]/)[0];
    try { value = decodeURIComponent(value); } catch (error) { return null; }
    value = value.replace(/^\.\//, '').replace(/^\//, '');
    if (value.split('/').some(part => !part || part === '.' || part === '..') || /[\\:\u0000-\u001f\u007f]/.test(value)) return null;
    return value || null;
  }
  function add(reference, key) {
    if (!reference || references.has(reference)) return;
    const candidate = key || keyFor(reference);
    if (candidate) { references.set(reference, candidate); referenceCharacters += reference.length; }
    if (references.size > 2048 || referenceCharacters > 256 * 1024) throw Error('Too many preview asset references.');
  }
  const cssReferences = text => {
    for (const match of text.matchAll(/url\(\s*(?:"([^"]*)"|'([^']*)'|([^\s"'()]*))\s*\)/gi)) add(match[1] === undefined ? (match[2] === undefined ? match[3] : match[2]) : match[1]);
  };
  for (const node of root.querySelectorAll('*')) {
    const tag = node.localName;
    if (['img','source','dp-inline-svg'].includes(tag)) {
      const key = keyFor(node.getAttribute('data-image'));
      if (key) {
        add('data-image:' + node.getAttribute('data-image'), key);
        const currentSource = node.getAttribute('src') || '';
        if (currentSource.startsWith('blob:')) add(currentSource, key);
      }
      add(node.getAttribute('src'));
      for (const entry of (node.getAttribute('srcset') || '').split(',')) add(entry.trim().split(/\s+/)[0]);
    }
    cssReferences(node.getAttribute('style') || '');
  }
  cssReferences(css);
  const assets = [], aliases = [], loaded = new Map();
  let total = 0;
  async function load(key) {
    if (!loaded.has(key)) {
      let blob = await imageStorage.getItem(key);
      // Exported references prefix keys with images/; local imports retain keys.
      if (!(blob instanceof Blob) && /^(?:images|fonts)\//.test(key)) {
        const id = await load(key.replace(/^(?:images|fonts)\//, ''));
        loaded.set(key, id); return id;
      }
      if (!(blob instanceof Blob)) { loaded.set(key, null); return null; }
      let type = blob.type === 'image/jpg' ? 'image/jpeg' : blob.type;
      if (!type || type === 'application/octet-stream') type = mimeTypes[key.split('.').pop().toLowerCase()] || '';
      if (!allowedType.test(type)) { loaded.set(key, null); return null; }
      if (blob.size > 8 * 1024 * 1024 || assets.length >= 128 || total + blob.size > 32 * 1024 * 1024) throw Error('Preview assets exceed their limits.');
      // Collapse aliases of the same stored Blob, including images/name and name.
      let asset = assets.find(item => item.original === blob);
      if (!asset) {
        asset = {id:'p' + assets.length, blob:blob.type === type ? blob : blob.slice(0, blob.size, type), original:blob};
        assets.push(asset); total += blob.size;
      }
      loaded.set(key, asset.id);
    }
    return loaded.get(key);
  }
  for (const [reference, key] of references) {
    const id = await load(key);
    if (id) aliases.push({reference, id});
  }
  return {html, css, aliases, assets:assets.map(({id,blob}) => ({id,blob}))};
}
